import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { api, fmtEUR, API } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Reveal, SectionHeader } from "../components/home/Sections";

export default function BoutiquePage() {
    const { data } = useSite();
    const [catalog, setCatalog] = useState(null);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const hero = data?.sections?.boutique_hero || {};

    useEffect(() => {
        api.get("/public/boutique").then((r) => setCatalog(r.data)).catch(() => setCatalog({ products: [], categories: [] }));
    }, []);

    const products = (catalog?.products || []).filter(
        (p) =>
            (!category || p.category === category) &&
            (!search || p.name.toLowerCase().includes(search.toLowerCase()) || p.short_description.toLowerCase().includes(search.toLowerCase()))
    );

    return (
        <main className="bg-ivory min-h-screen pt-32 pb-24" data-testid="boutique-page">
            <div className="container-bc">
                <SectionHeader
                    eyebrow="La Boutique"
                    title={hero.title || "La Boutique BODYCOACHIA"}
                    subtitle={hero.subtitle}
                />
                <Reveal className="flex flex-col sm:flex-row gap-3 mb-10">
                    <div className="relative flex-1 max-w-md">
                        <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-soft" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher un produit…"
                            className="w-full rounded-full border border-border bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
                            data-testid="boutique-search"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setCategory("")} className={`px-4 py-2 rounded-full text-sm border transition-colors ${!category ? "bg-forest text-ivory border-forest" : "border-border text-ink-soft hover:border-forest/40"}`} data-testid="filter-all">
                            Tout
                        </button>
                        {(catalog?.categories || []).map((c) => (
                            <button key={c} onClick={() => setCategory(c === category ? "" : c)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${category === c ? "bg-forest text-ivory border-forest" : "border-border text-ink-soft hover:border-forest/40"}`} data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                {c}
                            </button>
                        ))}
                    </div>
                </Reveal>

                {catalog === null ? (
                    <p className="text-ink-soft">Chargement…</p>
                ) : products.length === 0 ? (
                    <div className="rounded-3xl border-2 border-dashed border-gold/50 bg-pearl p-12 text-center" data-testid="boutique-empty">
                        <p className="font-heading font-bold text-ink text-xl">Aucun produit dans cette sélection</p>
                        <p className="text-ink-soft mt-2 text-sm">Le catalogue est alimenté par notre équipe — de nouveaux équipements arrivent prochainement.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {products.map((p, i) => (
                            <Reveal key={p.product_id} delay={i * 0.07}>
                                <Link to={`/boutique/${p.slug}`} className="group card-light overflow-hidden flex flex-col h-full hover:-translate-y-1 transition-transform duration-300" data-testid={`product-card-${p.slug}`}>
                                    <div className="relative aspect-[4/3] bg-pearl overflow-hidden">
                                        {p.image ? (
                                            <img src={`${API}/shop-media/${p.product_id}/${p.image.id}`} alt={p.image.title || p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-ink-soft text-sm">Visuel à venir</div>
                                        )}
                                        {p.featured && (
                                            <span className="absolute top-3 left-3 bg-gold text-white text-[11px] font-bold px-3 py-1 rounded-full">Produit vedette</span>
                                        )}
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <p className="text-[11px] font-mono uppercase tracking-widest text-gold-dark">{p.category}</p>
                                        <h2 className="font-heading font-bold text-ink text-lg mt-1.5 leading-snug">{p.name}</h2>
                                        <p className="text-ink-soft text-sm mt-2 leading-relaxed flex-1">{p.short_description}</p>
                                        <div className="mt-5 flex items-center justify-between">
                                            {p.price_cents != null ? (
                                                <span className="font-heading font-extrabold text-forest text-xl">{fmtEUR(p.price_cents, p.currency)}</span>
                                            ) : (
                                                <span className="text-xs text-ink-soft">Prix communiqué par le marchand</span>
                                            )}
                                            <span className="inline-flex items-center gap-1.5 text-forest font-semibold text-sm group-hover:text-gold-dark transition-colors">
                                                Découvrir <ArrowRight size={15} />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                )}
                <Reveal className="mt-16 rounded-3xl bg-pearl border border-border p-8 text-center">
                    <p className="text-sm text-ink-soft max-w-2xl mx-auto leading-relaxed">
                        La Boutique BODYCOACHIA est une vitrine : chaque achat est finalisé chez le marchand partenaire (Amazon ou autre).
                        Certains liens peuvent être des liens affiliés, sans surcoût pour vous.
                    </p>
                </Reveal>
            </div>
        </main>
    );
}
